import TextInput from "../UI/TextInput";
import { FieldArray, Form, Formik } from "formik";
import Button from "../UI/Button";
import {
  PROFILE_BAILLEUR_INITIAL_VALUE,
  PROFILE_INITIAL_VALUE,
  profileBailleurSchema,
  profileMandataireSchema,
  StatusFiscal,
  StatusFiscalDescription,
  TypeSociete,
} from "../../models/profile";
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
import { useState } from "react";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import { ModalDialogFooter } from "../UI/ModalDialog";
import { Interrogation, Trash } from "../UI/Icons";
import { useNavigate } from "react-router-dom";
import Tooltip from "../UI/Tooltip";
import { handleAPIError } from "../../utils/index.jsx";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import MandataireFormFields from "../Account/MandataireFormFields";
import useAccount from "../../hooks/use-account";
import useMandataire from "../../hooks/use-mandataire";
import TextArea from "../UI/TextArea";
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import Checkbox from "../UI/Checkbox";
import { ReactSelect } from "../UI/Select";
import ButtonCard from "../UI/ButtonCard";
import ProfileFormPerso from "./ProfileFormPerso";
import ProfileFormDom from "./ProfileFormDom";
import { produce } from "immer";
import SelectIban from "../UI/SelectIban";
import SiretFields from "../UI/SiretFields";

import useAuth from "../../hooks/use-auth.js";

const commonOptions = [
  {
    label: StatusFiscalDescription[StatusFiscal.NUMICRO],
    value: StatusFiscal.NUMICRO,
  },
  {
    label: StatusFiscalDescription[StatusFiscal.NUREEL],
    value: StatusFiscal.NUREEL,
  },
];

const optionsSociete = [
  ...commonOptions,
  {
    label: StatusFiscalDescription[StatusFiscal.IS],
    value: StatusFiscal.IS,
  },
];

const optionsIndiv = [
  ...commonOptions,
  {
    label: StatusFiscalDescription[StatusFiscal.LMNPMICRO],
    value: StatusFiscal.LMNPMICRO,
  },
  {
    label: StatusFiscalDescription[StatusFiscal.LMNPREEL],
    value: StatusFiscal.LMNPREEL,
  },
];

export default function ProfileBailleurForm({ profileBailleur, onSubmit }) {
  const { auth } = useAuth();
  const { updateProfileBailleur, addProfileBailleur, deleteProfileBailleur } =
    useProfileBailleurs();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const { account } = useAccount();
  const { mandataire } = useMandataire();
  const edit = !!profileBailleur;

  if (!account) return null;

  async function submitHandler(values, { setSubmitting }) {
    const sanitizedValues = { ...sanitizeValues(values) };
    // Le numéro de SIRET est celui définit dans le form profile_nom, et on
    // l'applique à tous les profils. On ne gère pas le multipropriétaire
    // si c'est une société
    const profile = produce(sanitizedValues, (draft) => {
      draft.profiles.forEach(
        (profile) => (profile.siret = sanitizedValues.siret),
      );
    });
    try {
      if (edit) {
        await updateProfileBailleur(parseInt(profileBailleur.id), profile);
      } else {
        await addProfileBailleur(profile);
      }
      setError("");
      onSubmit();
    } catch (error) {
      setError(handleAPIError(error));
    }
    setSubmitting(false);
  }

  let validationSchema = profileBailleurSchema;
  let initialValues = toInitialValuesFromSchema(
    profileBailleurSchema,
    profileBailleur,
    PROFILE_BAILLEUR_INITIAL_VALUE,
  );
  if (account.home === "/pro") {
    validationSchema = profileMandataireSchema;
    // Use profileBailleur's remuneration if set, otherwise fallback to mandataire's default
    initialValues.remuneration_type =
      profileBailleur?.remuneration_type ?? mandataire?.remuneration_type;
    initialValues.remuneration_montant =
      profileBailleur?.remuneration_montant ?? mandataire?.remuneration_montant;
    initialValues.mandataire_tva =
      profileBailleur?.mandataire_tva ?? mandataire?.tva;
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={submitHandler}
        validationSchema={validationSchema}
      >
        {({ values, resetForm, setFieldValue }) => (
          <Form>
            <p className={"text-center"}>
              <img src={"/photo_profil_bailleur.png"} alt={"Profil bailleur"} />
            </p>
            <h2 className="text-center">
              {edit
                ? "Modifier le profil bailleur"
                : "Créer un profil bailleur"}
            </h2>
            <TextInput label="Nom du profil" name="nom_profile" />
            <Checkbox
              name="is_societe"
              className={"marginT10 marginB10"}
              onChange={(e) => {
                resetForm({
                  values: {
                    ...PROFILE_BAILLEUR_INITIAL_VALUE,
                    nom_profile: initialValues.nom_profile,
                    cc_mail: initialValues.cc_mail,
                    notes: initialValues.notes,
                    rue: initialValues.rue,
                    cp: initialValues.cp,
                    ville: initialValues.ville,
                    pays: initialValues.pays,
                    is_societe: e.target.checked,
                  },
                });
                if (e.target.checked) {
                  setFieldValue("profiles[0].type", "2");
                } else {
                  setFieldValue("profiles[0].type", "1");
                }
              }}
            >
              Il s'agit d'une societe (SCI IR / IS, SAS, ...)
            </Checkbox>
            <ReactSelect
              name={"regime_fiscal"}
              label={"Régime fiscal"}
              className={"marginB10"}
              options={values.is_societe ? optionsSociete : optionsIndiv}
            />
            {([
              StatusFiscal.LMNPMICRO,
              StatusFiscal.LMNPREEL,
              StatusFiscal.IS,
            ].includes(values.regime_fiscal) ||
              values.is_societe) && (
              <>
                <SiretFields
                  nameSiret="siret"
                  nameNomSociete="profiles[0].nom_societe"
                  nameNom="profiles[0].nom"
                  namePrenom="profiles[0].prenom"
                  nameAdresse="profiles[0].adresse"
                  nameVille="profiles[0].ville"
                  nameCp="profiles[0].cp"
                  label="SIRET / Nom de l'entreprise"
                  placeholder="12345678901234 ou Nom de l'entreprise"
                  mandatory
                />
                <TextInput
                  name={"numero_tva"}
                  label={"N° TVA intracommunautaire"}
                  placeholder={"FR70911182582"}
                />
                <TextInput
                  type="date"
                  name={"debut_activite"}
                  label={"Début d'activité comptable"}
                />
              </>
            )}
            {account.home === "/pro" && <MandataireFormFields />}
            {values.is_societe && (
              <Callout variant="warning">
                Il faut indiquer ci-dessous les informations concernant
                l'entreprise (gérant, etc), et non pas ajouter les associés.
              </Callout>
            )}
            <FieldArray
              name="profiles"
              render={(arrayHelpers) => (
                <div>
                  {values.profiles.map((item, index) => (
                    <Card padding={"md"} key={index} className={"marginB10"}>
                      <ProfileFormPerso
                        prefix={`profiles[${index}].`}
                        type={values.profiles[index].type}
                        isBailleur
                      />
                      <ProfileFormDom
                        isBailleur
                        prefix={`profiles[${index}].`}
                      />
                      {index !== 0 && (
                        <div className={"text-right"}>
                          <ButtonCard
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            <Trash />
                          </ButtonCard>
                        </div>
                      )}
                    </Card>
                  ))}
                  <Button
                    className={"marginB10"}
                    variant="tertiary"
                    type="button"
                    onClick={() => arrayHelpers.push(PROFILE_INITIAL_VALUE)}
                  >
                    + Ajouter un copropriétaire
                  </Button>
                </div>
              )}
            />
            {values.profiles.some(
              (profile) => profile.socType === TypeSociete.SCI,
            ) && (
              <Checkbox name="sci_famille" className={"marginB10"}>
                SCI Familiale
              </Checkbox>
            )}
            <TextInput
              name={"cc_mail"}
              placeholder={"gestionaire1@qalimo.fr,gestionaire2@qalimo.fr"}
              label={
                <>
                  Adresses e-mail en copie{" "}
                  <Tooltip
                    content={
                      "Indiquez ici, séparés par une virgule, les adresses mail qui seront en copie de tous les mails envoyés. A noter que pour les signatures électroniques, c'est le mail principal des profils qui sera utilisé."
                    }
                  >
                    <Interrogation />
                  </Tooltip>
                </>
              }
            />
            <TextInput
              name={"ics_id"}
              label={
                <>
                  Numéro ICS{" "}
                  <Tooltip
                    content={
                      "Nécessaire pour pouvoir faire des prélèvements de loyer"
                    }
                  >
                    <Interrogation />
                  </Tooltip>
                </>
              }
            />
            <SelectIban />
            <TextInput
              name={"bic"}
              label={"BIC"}
              transformValue={(v) => v.toUpperCase()}
            />
            <TextArea
              type="text"
              name="notes"
              label="Notes"
              rows="4"
              placeholder="Identifiant CAF, VISAL, information concernant ce profil, etc."
            />

            <Callout variant="danger">{error}</Callout>
            <ModalDialogFooter>
              {auth.userId === profileBailleur?.id_compte && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Voulez-vous vraiment supprimer ce profil ? Tous les biens / locataires / configurations rattachés à ce profil seront également supprimés.",
                      )
                    ) {
                      deleteProfileBailleur(profileBailleur.id);
                      nav("../");
                    }
                  }}
                >
                  Supprimer
                </Button>
              )}
              <FormikSubmitButton>Valider</FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        )}
      </Formik>
    </>
  );
}
